/**
 * Pure edit-eligibility / edit-state helpers for the CommentsPanel, extracted so
 * the branching is unit-testable in isolation from the Redux/restClient-connected
 * component (mirrors commentRead.ts / commentScroll.ts).
 */

/** The minimal comment shape the edit helpers need. */
export interface EditableComment {
    author_identity_id: string | null;
}

/**
 * Whether the current identity may edit this comment in place. The Console
 * exposes edit only for the viewer's OWN comment (editing another person's
 * comment is out of scope). The backend additionally permits a caller with
 * UPDATE on the parent record, but the Console intentionally does not surface
 * that — own comments only, matching the feature ask.
 */
export const canEditComment = (
    comment: EditableComment,
    currentIdentityId?: string
): boolean => !!currentIdentityId && comment.author_identity_id === currentIdentityId;

/**
 * Whether an edit draft is savable: a comment body is NOT NULL server-side, so a
 * blank / whitespace-only draft is rejected (matches the composer's post guard).
 */
export const canSaveEdit = (draft: string): boolean => draft.trim().length > 0;

/**
 * Whether an in-place edit has unsaved changes (drives the navigate-away guard):
 * a comment is being edited AND the draft diverges from the body it started from.
 * An unchanged edit is not a pending change, so re-opening an edit and closing it
 * without typing never blocks navigation.
 */
export const hasUnsavedEdit = (
    editingId: string | null,
    draft: string,
    original: string
): boolean => editingId !== null && draft !== original;

/**
 * What to do with the CURRENTLY-open in-place edit when the user moves to a
 * DIFFERENT comment input — opens another comment's edit, or focuses the
 * new-comment composer. Margot's rule (2026-09-24): only one comment may hold
 * unsaved content at any one time, and moving away from a dirty edit must SAVE
 * it rather than silently drop it (the three data-loss paths in
 * operator-task-fe-comment-edit-unsaved-changes-data-loss).
 *  - "save":    a changed, savable (non-empty) edit → commit it before switching.
 *  - "discard": a changed but UN-savable edit (cleared to empty — a comment body
 *               is NOT NULL server-side, so it cannot be saved) → the empty draft
 *               holds no persistable content, so closing it loses nothing.
 *  - "none":    no edit open, or the edit is unchanged → nothing to persist.
 */
export type PendingEditTransition = "save" | "discard" | "none";

export const resolvePendingEditTransition = (
    editingId: string | null,
    draft: string,
    original: string
): PendingEditTransition => {
    if (!hasUnsavedEdit(editingId, draft, original)) {
        return "none";
    }
    return canSaveEdit(draft) ? "save" : "discard";
};

/**
 * What to do with an OPEN in-place editor when the user moves the active input to
 * the new-comment composer. Enforces Margot's "no two comments ever edited at the
 * same time" rule (2026-09-30): the open editor must be resolved so it cannot
 * linger and later diverge into a second unsaved item.
 *  - "none":  no editor is open — nothing to do.
 *  - "save":  the editor has savable (non-empty) unsaved changes — commit it.
 *  - "close": the editor is unchanged, or was cleared to an unsavable empty body
 *             — close it (nothing persistable is lost).
 * Distinct from resolvePendingEditTransition in that an open-but-unchanged editor
 * yields "close" (not "none"): moving to the composer must actively CLOSE it.
 */
export type ComposerFocusEditorAction = "none" | "save" | "close";

export const editorActionOnComposerFocus = (
    editingId: string | null,
    draft: string,
    original: string
): ComposerFocusEditorAction => {
    if (editingId === null) {
        return "none";
    }
    return resolvePendingEditTransition(editingId, draft, original) === "save"
        ? "save"
        : "close";
};
